import { useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { useSelector } from 'react-redux';
import { StoreInterface } from '../../../store/types';
import usePaymentFlow from '../../../hooks/payment/usePaymentFlow';
import { usePaymentCoordinator } from '../../../hooks/payment/usePaymentCoordinator';
import {
  AppWebViewMessageTypes,
  PaymentGateway,
  PaymentMethod,
  PaymentStatus,
} from '../../../services/exports/Constants';
import { useElements, useStripe } from '@stripe/react-stripe-js';
import useMobileApp from '../../../hooks/mobile-app/useMobileApp';
import { ButtonTypes, Order, PaymentResult } from '../../../services/exports/Interfaces';
import { PaymentIntent } from '@stripe/stripe-js';
import WebViewResource from '../../../services/resources/WebViewResource';
import CustomButton from '../../buttons/CustomButton';
import { ChildPayButtonProps, handleStripeNextAction, stripeResultCaster } from './types';
import { PaymentReturnUrlPlaceholder } from '../../../hooks/payment/useCheckout';
import useBillingInfo from '../../../hooks/payment/useBillingInfo';
import classnames from 'classnames';
import paymentLog from '../../../services/helpers/PaymentLogger';
import { StripeWallet, paymentMethodFromStripeType } from '../../../utils/payment-options-visibility';
import { useStripeElementsMounted, useStripeElementsSession } from '../StripeElementsMounted';

/**
 * `useStripe()` throws outside `<Elements>`, and the ErrorBoundary then takes the whole checkout
 * down (SHOP-WEB-2P). The coordinator mounts `<Elements>` only while Stripe is active (and, as an
 * Adyen restaurant's secondary gateway, has its PayPal intent), so a Stripe-routed method can
 * briefly (or, on a stale selection, lastingly) render without it: show an inert button until it
 * is there instead.
 */
export default function StripePayButton(props: ChildPayButtonProps) {
  const elementsMounted = useStripeElementsMounted();
  const { pending } = useStripeElementsSession();

  // The Elements wait for the customer's session: a moment of an inert button, nothing to report.
  if (pending) {
    return <InertStripePayButton {...props} />;
  }

  if (!elementsMounted) {
    return <StripePayButtonWithoutElements {...props} />;
  }

  return <StripePayButtonContent {...props} />;
}

function StripePayButtonWithoutElements(props: ChildPayButtonProps) {
  const { company } = useSelector((state: StoreInterface) => state.initialData);
  const { selectedPaymentMethod } = usePaymentFlow();

  // Once per mount: the selection can change while <Elements> is missing, and each change is not news.
  useEffect(() => {
    paymentLog.warn('stripe', 'pay_button_without_elements', {
      company_id: company?.id,
      payment_method: selectedPaymentMethod,
    });
  }, []);

  return <InertStripePayButton {...props} />;
}

function InertStripePayButton({ title, className }: ChildPayButtonProps) {
  return (
    <>
      <span data-test="pay-button-state" data-ready="false" className="hidden" />
      <CustomButton
        testId="pay-button"
        buttonType={ButtonTypes.primary}
        title={title}
        disabled
        onClick={() => undefined}
        showArrow
        className={className}
      />
    </>
  );
}

/** How long Stripe.js may take to resolve inside <Elements> before the button is reported as dead. */
export const STRIPE_READY_TIMEOUT_MS = 10_000;

function StripePayButtonContent({
  title,
  loading,
  disabled,
  silentlyDisabled,
  setLoading,
  onClick,
  generateReturnUrl,
  className,
}: ChildPayButtonProps) {
  const { company } = useSelector((state: StoreInterface) => state.initialData);
  const { t } = useTranslation(undefined, { keyPrefix: 'Components:Payment:PaymentCoordinator' });

  const {
    selectedPaymentMethod,
    findSavedPaymentMethod,
    onlinePaymentsGateway,
    isSavedCardSelected,
    stripeElementType,
  } = usePaymentFlow();
  const { paymentGatewaySDK, basketTotal } = usePaymentCoordinator(PaymentGateway.Stripe);
  const { paymentIntent, platformStripe } = paymentGatewaySDK;
  const stripe = useStripe();
  const elements = useElements();
  const { isMobileApp } = useMobileApp();
  const { stripeFormat, billingPhoneNumber } = useBillingInfo();

  const elementSubmitInFlightRef = useRef(false);

  // An Adyen restaurant's PayPal through Stripe: the one method this button pays when Stripe is not the
  // primary gateway (StripeSecondaryElements in the coordinator).
  const isStripeSecondary = onlinePaymentsGateway !== PaymentGateway.Stripe;

  // Who is paying, without the blanks Stripe would refuse: the Elements' token and PayPal's confirm.
  const billingDetails = useMemo(
    () =>
      Object.fromEntries(
        Object.entries({ name: stripeFormat.name, email: stripeFormat.email, phone: billingPhoneNumber }).filter(
          ([, value]) => typeof value === 'string' && value !== '',
        ),
      ),
    [stripeFormat.name, stripeFormat.email, billingPhoneNumber],
  );

  const isWalletMethod =
    selectedPaymentMethod === PaymentMethod.ApplePay || selectedPaymentMethod === PaymentMethod.GooglePay;

  // A tap on the Express Checkout Element's wallet selects that wallet (StripeElementsPaymentMethods):
  // its sheet pays, never this button, which only points to the wallet button meanwhile. Link is one
  // of those wallets too, unless it is the Payment Element's own Link row (which reports its type).
  const isExpressCheckoutWallet =
    !isStripeSecondary &&
    !isMobileApp &&
    (isWalletMethod || (selectedPaymentMethod === PaymentMethod.Link && stripeElementType !== 'link'));

  // Whatever is not a row of this shop's own is the Payment Element's, confirmed through it against an
  // intent the backend creates at order time. On the web the wallets belong to the Express Checkout
  // Element, which confirms by itself.
  const paysThroughPaymentElement =
    !isStripeSecondary &&
    !isSavedCardSelected &&
    selectedPaymentMethod !== PaymentMethod.Cash &&
    selectedPaymentMethod !== PaymentMethod.Paypal &&
    !isWalletMethod &&
    !isExpressCheckoutWallet;

  async function confirmPayment(order: Order, paymentIntent: PaymentIntent): Promise<PaymentResult> {
    // Both placeholders, not just the uuid: without the gateway the return_url shipped to Stripe
    // carried a literal `payment_gateway={placeholder::payment_gateway}` (caught by the stubbed
    // e2e, which is the only place the built URL is observable). The Adyen path substitutes both.
    const returnUrl = generateReturnUrl({
      [PaymentReturnUrlPlaceholder.OrderUUID]: order.uuid,
      [PaymentReturnUrlPlaceholder.PaymentGateway]: PaymentGateway.Stripe,
    });

    // The order exists by now, so a Stripe.js that never resolved must fail the payment the way
    // any other refusal does, not throw a TypeError on `stripe.confirm…` (SHOP-WEB-1S).
    if (!stripe && requiresStripeToConfirm()) {
      paymentLog.issue('stripe', 'confirm_stripe_not_ready', {
        order_uuid: order?.uuid,
        company_id: company?.id,
        payment_method: selectedPaymentMethod,
        intent_id: paymentIntent?.id,
      });

      return { status: PaymentStatus.Failed, error: null, result: null };
    }

    paymentLog.info('stripe', 'confirm_payment_start', {
      order_uuid: order?.uuid,
      company_id: company?.id,
      payment_method: selectedPaymentMethod,
      intent_id: paymentIntent?.id,
    });

    const handler =
      {
        [PaymentMethod.Cash]: () => confirmCashPayment(),
        [PaymentMethod.Paypal]: () =>
          isStripeSecondary
            ? confirmStripeSecondaryPaypalPayment(paymentIntent, returnUrl, order)
            : confirmPaypalPayment(paymentIntent, returnUrl, order),
        ...(isMobileApp
          ? {
              [PaymentMethod.ApplePay]: () => confirmWalletPaymentNative(paymentIntent, order),
              [PaymentMethod.GooglePay]: () => confirmWalletPaymentNative(paymentIntent, order),
            }
          : {}),
      }[selectedPaymentMethod] ??
      (isSavedCardSelected
        ? () => confirmSavedCardPayment(paymentIntent, order)
        : () => confirmPaymentElementPayment(paymentIntent, order));

    return await handler();
  }

  /** Cash settles offline and the native app confirms wallets itself; everything else calls Stripe.js. */
  function requiresStripeToConfirm(): boolean {
    if (selectedPaymentMethod === PaymentMethod.Cash) {
      return false;
    }

    return !(isWalletMethod && isMobileApp);
  }

  /** The card on file, confirmed with its stored id on the restaurant's account. */
  async function confirmSavedCardPayment(paymentIntent: PaymentIntent, order: Order): Promise<PaymentResult> {
    const savedCard = findSavedPaymentMethod(PaymentMethod.Card);

    if (!savedCard?.external_payment_method_id) {
      paymentLog.issue('stripe', 'confirm_saved_card_without_card', {
        order_uuid: order?.uuid,
        company_id: company?.id,
        intent_id: paymentIntent?.id,
      });

      return { status: PaymentStatus.Failed, error: null, result: null };
    }

    const result = stripeResultCaster(
      await stripe.confirmCardPayment(paymentIntent.client_secret, {
        payment_method: savedCard.external_payment_method_id,
      }),
    );

    logStripeConfirmResult('confirm_card_payment', result, order, paymentIntent, { saved_card: true });

    return result;
  }

  /**
   * The backend confirmed the order's intent with the ConfirmationToken the order carried, and answers
   * 402 only when the customer has something left to do — a 3DS challenge, run in the page, or a
   * bank's page, which comes back to the order page with Stripe's redirect_status. A payment the
   * confirm settled is a plain order response and never reaches here.
   */
  async function confirmPaymentElementPayment(paymentIntent: PaymentIntent, order: Order): Promise<PaymentResult> {
    if (paymentIntent?.status !== 'requires_action') {
      paymentLog.issue('stripe', 'payment_element_unexpected_intent', {
        order_uuid: order?.uuid,
        company_id: company?.id,
        intent_id: paymentIntent?.id,
        intent_status: paymentIntent?.status,
      });

      return { status: PaymentStatus.Failed, error: null, result: null };
    }

    const result = await handleStripeNextAction(stripe, paymentIntent);

    logStripeConfirmResult('payment_element_next_action', result, order, paymentIntent);

    return result;
  }

  async function confirmPaypalPayment(
    paymentIntent: PaymentIntent,
    returnUrl: string,
    order: Order,
  ): Promise<PaymentResult> {
    const { client_secret } = paymentIntent;
    const attachedPaypalAccount = findSavedPaymentMethod(PaymentMethod.Paypal);

    // The Elements run Stripe.js as the restaurant's account; PayPal's intent lives on the platform, so
    // it is confirmed through the platform instance the coordinator holds.
    const stripeForPaypal = await platformStripe;

    if (!stripeForPaypal) {
      paymentLog.issue('stripe', 'confirm_paypal_without_platform_stripe', {
        order_uuid: order?.uuid,
        company_id: company?.id,
        intent_id: paymentIntent?.id,
      });

      return { status: PaymentStatus.Failed, error: null, result: null };
    }

    // PayPal needs only the return_url and returns the payer itself; the shop names who is paying
    // and asks for no address (PayPal's address checks are about shipping, which is never sent).
    const result = stripeResultCaster(
      await stripeForPaypal.confirmPayPalPayment(client_secret, {
        return_url: returnUrl,
        payment_method: attachedPaypalAccount
          ? attachedPaypalAccount.external_payment_method_id
          : { billing_details: billingDetails },
      }),
    );

    logStripeConfirmResult('confirm_paypal_payment', result, order, paymentIntent, {
      saved_paypal: Boolean(attachedPaypalAccount),
    });

    return result;
  }

  /**
   * An Adyen restaurant's PayPal through Stripe: confirmed on the Elements around its one intent, with
   * the same payer details and no address, as where Stripe is the primary gateway.
   */
  async function confirmStripeSecondaryPaypalPayment(
    paymentIntent: PaymentIntent,
    returnUrl: string,
    order: Order,
  ): Promise<PaymentResult> {
    const { client_secret } = paymentIntent;
    const attachedPaypalAccount = findSavedPaymentMethod(PaymentMethod.Paypal);
    const result = stripeResultCaster(
      await stripe.confirmPayPalPayment(client_secret, {
        return_url: returnUrl,
        payment_method: attachedPaypalAccount
          ? attachedPaypalAccount.external_payment_method_id
          : { billing_details: billingDetails },
      }),
    );

    logStripeConfirmResult('confirm_paypal_payment', result, order, paymentIntent, {
      saved_paypal: Boolean(attachedPaypalAccount),
    });

    return result;
  }

  // Native app: the wallet sheet is presented by the native layer, so we keep the
  // order-first flow and hand the intent off over the WebView bridge.
  async function confirmWalletPaymentNative(paymentIntent: PaymentIntent, order: Order): Promise<PaymentResult> {
    WebViewResource.sendMessage(AppWebViewMessageTypes.PaymentIntent, {
      payment_method: selectedPaymentMethod,
      payment_intent: paymentIntent,
      order,
      amount: Number(basketTotal),
      company,
    });

    return {
      status: PaymentStatus.Pending,
      error: null,
      result: null,
    };
  }

  async function confirmCashPayment(): Promise<{ success: true }> {
    return { success: true };
  }

  function logStripeConfirmResult(
    event: string,
    result: PaymentResult,
    order: Order,
    paymentIntent: PaymentIntent,
    extra?: Record<string, string | number | boolean | null | undefined>,
  ) {
    const errorInfo = result?.error as any;

    if (result?.status === PaymentStatus.Failed || errorInfo) {
      paymentLog.error('stripe', `${event}_failed`, {
        order_uuid: order?.uuid,
        company_id: company?.id,
        payment_method: selectedPaymentMethod,
        intent_id: paymentIntent?.id,
        error_type: errorInfo?.type,
        error_code: errorInfo?.code,
        decline_code: errorInfo?.decline_code,
        message: errorInfo?.message,
        ...extra,
      });

      return;
    }

    paymentLog.info('stripe', `${event}_done`, {
      order_uuid: order?.uuid,
      company_id: company?.id,
      payment_method: selectedPaymentMethod,
      intent_id: paymentIntent?.id,
      status: result?.status,
      ...extra,
    });
  }

  const _onClick = async () => {
    // The wallet's sheet is opened by its own button above, which this one cannot do for it. The
    // parent's checks still come first (an order outside the thresholds goes back to the menu); where
    // it would place the order, the customer is pointed to the wallet's button instead.
    if (isExpressCheckoutWallet) {
      return onClick(confirmPayment, paymentIntent, nudgeToWalletButton);
    }

    if (paysThroughPaymentElement) {
      return payThroughPaymentElement();
    }

    return onClick(confirmPayment, paymentIntent);
  };

  async function nudgeToWalletButton(): Promise<boolean> {
    paymentLog.info('stripe', 'express_wallet_pay_nudge', {
      company_id: company?.id,
      payment_method: selectedPaymentMethod,
    });
    toast.error(t('wallet_tap_above', { method: EXPRESS_WALLET_NAMES[selectedPaymentMethod] }));

    return false;
  }

  /**
   * Deferred mode, as the Express Checkout Element does it: the Element validates and shows its own
   * field errors (elements.submit), a ConfirmationToken is created from it, and only then is the
   * order placed with that token. The backend reads the method, the wallet and the save consent from
   * it, runs its fraud checks on them, and confirms the intent with that same token; the shop takes
   * part only when the customer has something left to do (confirmPaymentElementPayment).
   */
  async function payThroughPaymentElement(): Promise<boolean | void> {
    // Taken before the first await: a second tap while this one is in flight places no second order.
    if (elementSubmitInFlightRef.current) {
      return;
    }

    elementSubmitInFlightRef.current = true;
    setLoading(true);

    try {
      return await submitPaymentElement();
    } finally {
      elementSubmitInFlightRef.current = false;
    }
  }

  /**
   * What the customer is told when the Element refuses to submit or to tokenise. A validation error
   * gets our own words for the selected row where we have them, then Stripe's (localized through the
   * Elements locale), then a generic ask; anything else gets Stripe's words or "unavailable".
   * `messageKey` is null when Stripe's own message is shown.
   */
  function describeElementRejection(error: { type?: string; message?: string }): {
    message: string;
    messageKey: string | null;
  } {
    if (error.type === 'validation_error') {
      const methodKey = stripeElementType ? ELEMENT_INCOMPLETE_KEYS[stripeElementType] : undefined;

      if (methodKey) {
        return { message: t(methodKey), messageKey: methodKey };
      }

      if (error.message) {
        return { message: error.message, messageKey: null };
      }

      return { message: t('element_incomplete'), messageKey: 'element_incomplete' };
    }

    if (error.message) {
      return { message: error.message, messageKey: null };
    }

    return { message: t('method_unavailable'), messageKey: 'method_unavailable' };
  }

  async function submitPaymentElement(): Promise<boolean | void> {
    const stopWith = (event: string, attrs: Record<string, string | number | boolean | null | undefined> = {}) => {
      paymentLog.info('stripe', event, { company_id: company?.id, payment_method: selectedPaymentMethod, ...attrs });
      setLoading(false);
    };

    if (!elements || !stripe) {
      return stopWith('payment_element_not_ready');
    }

    let tokenResult: Awaited<ReturnType<typeof stripe.createConfirmationToken>>;

    // Stripe.js throws (it does not return an error) when the Payment Element is gone by the time
    // the token is made: the Elements re-created under a tap in flight, e.g. on a new customer
    // session (SHOP-WEB-6H). Unhandled, that left the button spinning with no word to the customer.
    try {
      const { error } = await elements.submit();

      // Not every row marks its own field when it refuses (Pay by Bank leaves its bank grid as it
      // was), so the customer is always told what is missing.
      if (error) {
        const { message, messageKey } = describeElementRejection(error);
        stopWith('payment_element_submit_rejected', {
          error_type: error.type,
          error_code: error.code,
          message_key: messageKey,
        });
        toast.error(message);

        return;
      }

      tokenResult = await stripe.createConfirmationToken({
        elements,
        // Stripe owns the address (fields.billingDetails.address 'auto' asks only what the method
        // and country need); the shop only fills in who is paying.
        params: { payment_method_data: { billing_details: billingDetails } },
      });
    } catch (thrown) {
      paymentLog.warn('stripe', 'payment_element_threw', {
        company_id: company?.id,
        payment_method: selectedPaymentMethod,
        error_name: (thrown as Error)?.name,
        message: (thrown as Error)?.message,
      });
      setLoading(false);
      toast.error(t('payment_not_ready'));

      return;
    }

    const { error: tokenError, confirmationToken } = tokenResult;

    if (tokenError) {
      const rejection = describeElementRejection(tokenError);
      paymentLog.error('stripe', 'payment_element_create_confirmation_token_failed', {
        company_id: company?.id,
        payment_method: selectedPaymentMethod,
        error_type: tokenError.type,
        error_code: tokenError.code,
        message: tokenError.message,
        message_key: rejection.messageKey,
      });
      setLoading(false);
      toast.error(rejection.message);

      return;
    }

    const preview = confirmationToken.payment_method_preview;
    const type = preview?.type ?? stripeElementType ?? 'card';
    const wallet = preview?.card?.wallet?.type as StripeWallet | undefined;
    const elementWallet = wallet && ['apple_pay', 'google_pay', 'link'].includes(wallet) ? wallet : undefined;

    return onClick(confirmPayment, undefined, undefined, undefined, {
      // What the backend reads the method from, and confirms the intent with; the method below names
      // the order for the shop's own analytics and logs only.
      stripe_confirmation_token: confirmationToken.id,
      payment_method_id: paymentMethodFromStripeType(type, elementWallet),
      // Where a bank's page sends the customer back to; the backend fills in the order.
      return_url: generateReturnUrl({ [PaymentReturnUrlPlaceholder.PaymentGateway]: PaymentGateway.Stripe }),
      // The customer's tick in Stripe's own save box (CustomerSession), as the token carries it.
      should_save_payment_info: Boolean(confirmationToken.setup_future_usage),
    });
  }

  // The button stays silently disabled until Stripe.js resolves, so a tap cannot start a checkout
  // that would confirm on a null `stripe` (SHOP-WEB-1S).
  const isWaitingForStripe = !stripe && requiresStripeToConfirm();
  // The app's wallet sheet confirms the up-front intent over the bridge. The Elements mount without
  // one, so an intent that failed (or a self-heal that gave up) would leave a tap that only earns a
  // 422 for the missing payment_intent_id.
  const isWaitingForWalletIntent = isMobileApp && isWalletMethod && !paymentIntent;
  // Not silently disabled while an Express Checkout wallet is selected: a sheet refused or closed
  // without an event leaves the wallet selected, and a dead button would strand the customer.
  const isSilentlyDisabled = silentlyDisabled || isWaitingForStripe || isWaitingForWalletIntent;

  // Stripe.js that never loads (blocked script, network, insecure origin) would otherwise leave a Pay
  // button that does nothing and no trace of why. Reported once per mount, and the customer is told.
  const stripeNeverReadyReportedRef = useRef(false);

  useEffect(() => {
    if (!isWaitingForStripe || stripeNeverReadyReportedRef.current) {
      return;
    }

    const timer = setTimeout(() => {
      stripeNeverReadyReportedRef.current = true;
      paymentLog.issue('stripe', 'stripe_never_ready', {
        company_id: company?.id,
        payment_method: selectedPaymentMethod,
        waited_ms: STRIPE_READY_TIMEOUT_MS,
      });
      toast.error(t('method_unavailable'));
    }, STRIPE_READY_TIMEOUT_MS);

    return () => clearTimeout(timer);
  }, [isWaitingForStripe]);

  const showsApplePayLook = selectedPaymentMethod === PaymentMethod.ApplePay && !isExpressCheckoutWallet;
  // The wallet's own button above pays; this one says so instead of offering a payment it cannot make.
  const buttonTitle = isExpressCheckoutWallet
    ? t('wallet_pay_above', { method: EXPRESS_WALLET_NAMES[selectedPaymentMethod] })
    : title;
  const buttonType = showsApplePayLook ? ButtonTypes.secondary : ButtonTypes.primary;

  return (
    <>
      {/* Same markers as PayButton's DefaultButton, so the e2e specs can address the Stripe pay
          button and wait on its readiness the way they do for cash. `silentlyDisabled` never reaches
          the DOM otherwise, and a click landing while it is set is a silent no-op. */}
      <span
        data-test="pay-button-state"
        data-ready={!disabled && !isSilentlyDisabled && !loading ? 'true' : 'false'}
        className="hidden"
      />
      <CustomButton
        testId="pay-button"
        buttonType={buttonType}
        title={buttonTitle}
        disabled={disabled}
        silentlyDisabled={isSilentlyDisabled}
        onClick={_onClick}
        loading={loading}
        showArrow
        className={classnames(className, {
          'flex-row-reverse gap-sm': showsApplePayLook,
        })}
      />
    </>
  );
}

/**
 * Payment Element rows (their Stripe type) whose own input the customer most often leaves out. A
 * type not listed falls back to Stripe's message for the error. iDEAL, EPS and Przelewy24 ask for a
 * bank like Pay by Bank does; SEPA Direct Debit for an IBAN.
 */
const ELEMENT_INCOMPLETE_KEYS: Record<string, string> = {
  card: 'element_incomplete_card',
  pay_by_bank: 'element_incomplete_bank',
  ideal: 'element_incomplete_bank',
  eps: 'element_incomplete_bank',
  p24: 'element_incomplete_bank',
  sepa_debit: 'element_incomplete_sepa_debit',
};

/** Brand names, the same in every language. */
const EXPRESS_WALLET_NAMES: Partial<Record<PaymentMethod, string>> = {
  [PaymentMethod.ApplePay]: 'Apple Pay',
  [PaymentMethod.GooglePay]: 'Google Pay',
  [PaymentMethod.Link]: 'Link',
};
